import {
  AuthContext,
  capitalize,
  useApi,
  Payment,
  updateBonusSettings,
} from '@repo-level/core-front';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEventName } from 'api/analytics';
import { Button } from '../buttons';
import { PaymentMethod as PaymentMethodComp } from './PaymentMethod';
import { fetchPaymentMethods } from 'helpers/payments';
import { ModalDefaultProps } from 'components/Modal';
import { InputPromo } from './InputPromo';
import { DepositBonuses } from './DepositBonuses';
import { Spinner } from 'components/Spinner';
import { groupMemberBR } from 'helpers/user';
import { LocalStorageDeposit } from 'helpers/common';
import { useAnalytics } from 'helpers/analytics';

import {
  ScDepositContent,
  ScDepositNav,
  ScDepositRow,
  ScDepositTitle,
  ScDepositWrapper,
  ScNotFound,
  ScPaymentMethodCompWrapper,
  ScWrapperPromo,
} from './styled';
import { CRYPTO_METHOD } from './PaymentMethod/helpers';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Checkbox } from 'components/form/Checkbox';
import { PaymentsMethods } from './PaymentsMethods';
import { CurrencyUser } from './CurrencyUser';
import { LocalStoragePaymentMethod } from './helpers';
import { AddonsContext } from 'contexts/addons';
import { sendBonusReceiveSettingsEvent } from 'modules/googleAnalytics';
import { useUserCanIssueBonuses } from 'hooks/bonuses';
import { useMedia } from 'modules/common/hooks/useMedia';

const MARGIN_BOTTOM_DESK = 16;
const MARGIN_BOTTOM_MOB = 12;

export const Deposit: FC<ModalDefaultProps> = ({ onScrollTo }) => {
  const { t } = useTranslation();
  const { isMobile } = useMedia();
  const { handleChangeReceiveBonuses, pendingUpdateBonusSettings } = useContext(
    AddonsContext
  );

  const refDepositForm = useRef<HTMLDivElement>(null);

  const { user, pendingSubmitPayment } = useContext(AuthContext);

  const { sendEventToAnalytics } = useAnalytics();

  const isBonusRestricted = useMemo(() => groupMemberBR(user), [user]);

  const isUserCanIssueBonuses = useUserCanIssueBonuses();

  const [
    currentPaymentMethod,
    setCurrentPaymentMethod,
  ] = useState<Payment.Method>();

  const [
    requestPaymentMethods,
    { data: paymentMethods, pending: pendingPaymentMethods },
  ] = useApi(fetchPaymentMethods, undefined);

  const localPaymentMethod = LocalStorageDeposit.get().paymentMethod;

  const initialSelectedMethodId = LocalStoragePaymentMethod.get();

  const handleToggleBonus = useCallback(() => {
    const newStatus = !isUserCanIssueBonuses;
    handleChangeReceiveBonuses();

    sendBonusReceiveSettingsEvent({
      status: newStatus ? 'on' : 'off',
      source: 'deposit',
    });
  }, [isUserCanIssueBonuses, handleChangeReceiveBonuses]);

  const isLocalMethodAvailable = useMemo(() => {
    return (
      paymentMethods?.some((method) => method.id === localPaymentMethod?.id) ??
      false
    );
  }, [localPaymentMethod?.id, paymentMethods]);

  useEffect(() => {
    if (user?.currency == null) return;
    requestPaymentMethods(user?.currency);
  }, [requestPaymentMethods, user]);

  useEffect(() => {
    if (pendingPaymentMethods || !paymentMethods || paymentMethods.length === 0)
      return;

    if (localPaymentMethod && isLocalMethodAvailable) {
      const updatedLocal = paymentMethods.find(
        (m) => m.id === localPaymentMethod.id
      );

      if (updatedLocal) {
        setCurrentPaymentMethod(updatedLocal);
        return;
      }
    }

    if (initialSelectedMethodId) {
      const savedMethod = paymentMethods.find(
        (m) => m.id === initialSelectedMethodId
      );

      if (savedMethod) {
        setCurrentPaymentMethod(savedMethod);
        LocalStorageDeposit.push({ paymentMethod: savedMethod });
        return;
      }
    }

    const firstMethod = paymentMethods[0];
    setCurrentPaymentMethod(firstMethod);
    LocalStorageDeposit.push({ paymentMethod: firstMethod });
    LocalStoragePaymentMethod.push(firstMethod.id);

    return () => {
      setCurrentPaymentMethod(undefined);
    };
  }, [
    paymentMethods,
    pendingPaymentMethods,
    localPaymentMethod,
    isLocalMethodAvailable,
    initialSelectedMethodId,
  ]);

  const marginBottomCheckbox = isMobile
    ? MARGIN_BOTTOM_MOB
    : MARGIN_BOTTOM_DESK;

  const handleSelectPaymentMethods = useCallback(
    (method: Payment.Method) => {
      setCurrentPaymentMethod(method);
      LocalStorageDeposit.push({ paymentMethod: method });

      onScrollTo && onScrollTo(refDepositForm);
    },
    [onScrollTo]
  );

  const isDeposit = Payment.Action.Deposit;

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__FORM__APPEARANCE,
      attributes: {
        additionalData: JSON.stringify({ from: window.location.href }),
      },
    });
  }, [sendEventToAnalytics]);

  const isCryptoMethod = currentPaymentMethod?.type.includes(CRYPTO_METHOD);

  if (user?.currency == null) return null;

  return (
    <ScDepositWrapper>
      <ScDepositTitle>{t('glossary:deposit')}</ScDepositTitle>
      <ScDepositRow ref={refDepositForm}>
        <ScDepositNav>
          <h3 className="payment-method">{t('deposits:allPaymentMethod')}</h3>
          <div className="currency-payments">
            <div className="currency-user">
              <CurrencyUser />
            </div>
            <PaymentsMethods
              currentMethod={currentPaymentMethod}
              methods={paymentMethods}
              pending={pendingPaymentMethods}
              onSelect={handleSelectPaymentMethods}
            />
          </div>
        </ScDepositNav>
        <ScDepositContent
          ref={refDepositForm}
          isCryptoMethod={!!isCryptoMethod}
        >
          <ScPaymentMethodCompWrapper isDeposit={!!isDeposit}>
            {pendingPaymentMethods ? (
              <Spinner size={64} center />
            ) : currentPaymentMethod == null ? (
              <ScNotFound>
                {capitalize(t('deposits:notSelectedMethod'))}
              </ScNotFound>
            ) : (
              <PaymentMethodComp
                userCurrency={user.currency}
                paymentMethod={currentPaymentMethod}
                paymentAction={Payment.Action.Deposit}
                setCurrentPaymentMethod={setCurrentPaymentMethod}
              />
            )}
          </ScPaymentMethodCompWrapper>
          {!isBonusRestricted && (
            <ScWrapperPromo userBonuses={isUserCanIssueBonuses}>
              {isUserCanIssueBonuses && (
                <div className="title">{t('deposits:bonus.title')}</div>
              )}
              <FormGroup marginBottom={marginBottomCheckbox}>
                <Checkbox
                  key={`receiveBonuses-${isUserCanIssueBonuses}`}
                  label={t('deposits:inputPromo.receiveBonuses')}
                  field="receiveBonuses"
                  initialValue={isUserCanIssueBonuses}
                  onClick={handleToggleBonus}
                />
              </FormGroup>
              {isUserCanIssueBonuses && (
                <>
                  <DepositBonuses paymentMethod={currentPaymentMethod} />
                  <InputPromo
                    pending={pendingPaymentMethods}
                    pendingUpdateBonus={pendingUpdateBonusSettings}
                  />
                </>
              )}
            </ScWrapperPromo>
          )}
          {isDeposit && (
            <div className="deposit-wrapper">
              {!isCryptoMethod && (
                <div className="deposit-button">
                  <Button
                    green
                    xLarge
                    form="depositForm"
                    wide
                    type="submit"
                    pending={pendingSubmitPayment}
                  >
                    {t('glossary:deposit')}
                  </Button>
                </div>
              )}
              <p className="help-wrapper">
                {t('deposits:help.title')}
                <span id="intercomcustomLauncher">
                  {t('deposits:help.description')}
                </span>
              </p>
            </div>
          )}
        </ScDepositContent>
      </ScDepositRow>
    </ScDepositWrapper>
  );
};
